import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Eye, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import QuickViewModal from '../modals/QuickViewModal';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { formatPrice, calculateDiscount } from '../../utils/currency';
import { translateLabel } from '../../utils/labels';
import { useLocalization } from '../../contexts/LocalizationContext';
import { getOptimizedImageUrl, getImageSrcSet, PRODUCT_CARD_SIZES } from '../../utils/imageOptimization';

const ProductCard = ({ product, priority = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice: locFormatPrice, getPrice, getTranslation } = useLocalization();
  const [isHovered, setIsHovered] = useState(false);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  
  const isWishlisted = isInWishlist(product._id || product.id);

  const currentLang = i18n.language;
  const productName = getTranslation(product.name) || product.name?.[currentLang] || product.name?.es || product.name;
  
  // Use optimized images for better performance. We prefer the thumbnail
  // variant as the base `src` (cards are small) and rely on `srcset`+`sizes`
  // to pick the appropriate variant for higher-DPI / larger viewports.
  const firstImage = product.images?.[0];
  const productImage =
    getOptimizedImageUrl(firstImage, 'thumbnail') ||
    firstImage?.thumbnail ||
    firstImage?.medium ||
    firstImage?.original ||
    product.image;
  const imageSrcSet = getImageSrcSet(firstImage);

  // Second photo, shown on hover (desktop) as a quick alternate angle.
  // Falls back to the existing scale-only hover effect when there's only
  // one image.
  const secondImage = product.images?.[1];
  const secondProductImage = secondImage
    ? getOptimizedImageUrl(secondImage, 'thumbnail') ||
      secondImage?.thumbnail ||
      secondImage?.medium ||
      secondImage?.original
    : null;
  
  // Handle multilingual alt text
  const getImageAlt = (image) => {
    if (!image?.alt) return productName;
    if (typeof image.alt === 'string') return image.alt;
    // Multilingual alt object
    return getTranslation(image.alt) || image.alt[currentLang] || image.alt.es || productName;
  };

  // Get price in current currency
  const currentPrice = product.prices ? getPrice(product.prices) : product.price;
  const comparePrice = product.compareAtPrice;
  const hasDiscount = comparePrice && comparePrice > currentPrice;
  const discountPercentage = hasDiscount ? calculateDiscount(comparePrice, currentPrice) : 0;

  // Stock/price now live per size variant. A card can't know which size
  // the shopper wants, so: the card is "out of stock" only if EVERY size
  // is unavailable, and quick-add only fires directly for single-size
  // products — multi-size products route to the PDP instead so the
  // shopper picks a real (correctly priced/stocked) size.
  const sizeVariants = product.sizeVariants || [];
  const hasMultipleSizes = sizeVariants.length > 1;
  const isOutOfStock = sizeVariants.length > 0
    ? sizeVariants.every((sv) => {
        if (sv.isAvailable === false) return true;
        const svInv = sv.inventory || {};
        if (!svInv.trackInventory || svInv.allowBackorder) return false;
        return Math.max(0, (svInv.quantity ?? 0) - (svInv.reserved ?? 0)) === 0;
      })
    : false;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    if (isOutOfStock || isAddingToCart) return;
    if (hasMultipleSizes) {
      navigate(`/product/${product.slug}`);
      return;
    }
    setIsAddingToCart(true);
    try {
      // product.id is string version of MongoDB _id; single-size products
      // have exactly one variant, so its size is unambiguous.
      await addToCart(product.id, 1, sizeVariants[0]?.size ?? null);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleToggleWishlist = async (e) => {
    e.preventDefault();
    await toggleWishlist(product._id || product.id);
  };

  const handleQuickView = (e) => {
    e.preventDefault();
    setIsQuickViewOpen(true);
  };

  return (
    <>
      <div
        className="group relative transition-all duration-300"
        style={{ background: 'transparent', border: 'none' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Badges — admin-managed `labels` array. Falls back to legacy
            boolean flags (isBestSelling / isNew) when no labels are set. */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1" data-testid="product-card-labels">
          {(() => {
            const COLOR_TOKENS = {
              terracotta: { bg: 'var(--terracotta)', fg: '#fefcf7' },
              ink: { bg: 'var(--ink)', fg: '#fefcf7' },
              accent: { bg: 'var(--accent, #e8d9c5)', fg: 'var(--ink)' },
              sage: { bg: '#7a8b6f', fg: '#fefcf7' },
              cream: { bg: '#fefcf7', fg: 'var(--ink)' },
            };
            const labels = Array.isArray(product.labels) ? product.labels : [];
            // Legacy fallback so old products still show a badge.
            const fallback = [];
            if (!labels.length) {
              if (product.isBestSelling) fallback.push({ text: t('products.bestSeller'), color: 'ink' });
              if (product.isNew) fallback.push({ text: t('products.new'), color: 'accent' });
            }
            const items = labels.length ? labels : fallback;
            return (
              <>
                {items.map((lbl, i) => {
                  const tone = COLOR_TOKENS[lbl.color] || COLOR_TOKENS.terracotta;
                  return (
                    <Badge
                      key={`${lbl.text}-${i}`}
                      className=""
                      style={{
                        background: tone.bg,
                        color: tone.fg,
                        fontSize: '10px',
                        letterSpacing: '2px',
                        textTransform: 'uppercase',
                        fontWeight: 500,
                        padding: '4px 10px',
                        borderRadius: 0,
                        border: lbl.color === 'cream' ? '1px solid var(--line)' : 'none',
                      }}
                      data-testid={`product-label-${lbl.text.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {translateLabel(lbl.text, t)}
                    </Badge>
                  );
                })}
                {hasDiscount && (
                  <Badge
                    className="text-white"
                    style={{
                      background: 'var(--terracotta)',
                      fontSize: '10px',
                      letterSpacing: '2px',
                      textTransform: 'uppercase',
                      fontWeight: 500,
                      padding: '4px 10px',
                      borderRadius: 0,
                    }}
                  >
                    -{discountPercentage}%
                  </Badge>
                )}
              </>
            );
          })()}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={handleToggleWishlist}
          className="absolute top-3 right-3 z-10 flex items-center justify-center transition-all"
          style={{
            background: 'rgba(255,255,255,0.85)',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            border: 'none',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#fff')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.85)')}
          aria-label={t('productCardMore.addToWishlist')}
        >
          <Heart
            className={`h-4 w-4 transition-colors ${isWishlisted ? '' : ''}`}
            strokeWidth={1.5}
            style={{
              color: isWishlisted ? 'var(--terracotta)' : 'var(--ink-soft)',
              fill: isWishlisted ? 'var(--terracotta)' : 'none',
            }}
          />
        </button>

        {/* Product Image */}
        <Link
          to={`/product/${product.slug}`}
          className="relative block overflow-hidden"
          style={{ aspectRatio: '3/4', background: 'var(--line-soft)' }}
        >
          <img
            src={productImage}
            srcSet={imageSrcSet || undefined}
            sizes={imageSrcSet ? PRODUCT_CARD_SIZES : undefined}
            alt={getImageAlt(product.images?.[0])}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            fetchPriority={priority ? 'high' : 'auto'}
            className="w-full h-full object-cover max-w-full transition-transform duration-700 ease-out"
            style={{
              transform: isHovered ? 'scale(1.04)' : 'scale(1)',
              opacity: isHovered && secondProductImage ? 0 : 1,
              transition: 'transform 700ms ease-out, opacity 500ms ease-out',
            }}
          />
          {secondProductImage && (
            <img
              src={secondProductImage}
              alt={getImageAlt(secondImage)}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover max-w-full"
              style={{
                opacity: isHovered ? 1 : 0,
                transform: isHovered ? 'scale(1.04)' : 'scale(1)',
                transition: 'transform 700ms ease-out, opacity 500ms ease-out',
              }}
            />
          )}

          {/* Quick Actions Overlay — only Quick View (Share moved to product detail) */}
          <div
            className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
            style={{
              background: 'rgba(0,0,0,0.18)',
              opacity: isHovered ? 1 : 0,
            }}
          >
            <Button
              size="icon"
              variant="secondary"
              onClick={handleQuickView}
              className="rounded-full w-10 h-10 bg-white hover:bg-white/90"
              aria-label={t('products.quickView')}
              data-testid="product-card-quickview"
            >
              <Eye className="h-4 w-4" strokeWidth={1.5} style={{ color: 'var(--ink)' }} />
            </Button>
          </div>

          {/* ReBorn badge — subtle mark for pieces made from reclaimed
              materials. Small and out of the way in the corner; never
              competes with the labels/discount badges up top. Sits inside
              the card's own <Link>, so it navigates via onClick + stopPropagation
              rather than nesting a second <a>. */}
          {product.isReborn && (
            <img
              src="/reborn-logo.png"
              alt={t('products.rebornBadge')}
              title={t('products.rebornBadge')}
              className="absolute bottom-2 right-2 w-16 h-auto rounded-md bg-[#FEFCF7] px-1.5 py-1 shadow-sm cursor-pointer"
              data-testid="product-card-reborn-badge"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate('/reborn');
              }}
            />
          )}
        </Link>

        {/* Product Info */}
        <div className="pt-4 pb-2">
          {/* Category */}
          {product.category && (
            <Link
              to={`/shop/${product.categorySlug || product.category.toLowerCase()}`}
              className="transition-colors"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '2.5px',
                color: 'var(--muted-editorial)',
                display: 'block',
                marginBottom: '6px',
              }}
            >
              {product.category}
            </Link>
          )}

          {/* Product Name */}
          <Link to={`/product/${product.slug}`}>
            <h3
              className="line-clamp-2 transition-colors"
              style={{
                fontFamily: 'var(--font-display)',
                fontVariationSettings: "'opsz' 60, 'SOFT' 50",
                fontSize: '19px',
                fontWeight: 400,
                letterSpacing: '-0.3px',
                lineHeight: 1.2,
                color: 'var(--ink)',
                marginBottom: '6px',
              }}
            >
              {productName}
            </h3>
          </Link>

          {/* Price */}
          <div className="flex items-center gap-2 mb-3">
            <span
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--ink)',
              }}
            >
              {locFormatPrice(currentPrice)}
            </span>
            {hasDiscount && (
              <span
                className="line-through"
                style={{ fontSize: '13px', color: 'var(--muted-editorial)' }}
              >
                {locFormatPrice(comparePrice)}
              </span>
            )}
          </div>

          {/* Rating */}
          {product.averageRating > 0 && (
            <div className="flex items-center gap-1 mb-3">
              <div className="flex" style={{ color: 'var(--terracotta)' }}>
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    className="w-3.5 h-3.5"
                    style={{ fill: i < Math.floor(product.averageRating) ? 'currentColor' : 'var(--line)' }}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
              <span style={{ fontSize: '11px', color: 'var(--muted-editorial)' }}>
                ({product.reviewCount})
              </span>
            </div>
          )}

          {/* Add to Cart Button — editorial outline */}
          <button
            onClick={handleAddToCart}
            disabled={isOutOfStock || isAddingToCart}
            data-testid="product-card-add-to-cart"
            className="w-full transition-colors flex items-center justify-center gap-2"
            style={{
              background: 'transparent',
              color: (isOutOfStock || isAddingToCart) ? 'var(--ink-soft)' : 'var(--ink)',
              border: `1px solid ${(isOutOfStock || isAddingToCart) ? 'var(--ink-soft)' : 'var(--ink)'}`,
              padding: '12px',
              fontFamily: 'var(--font-body)',
              fontSize: '10px',
              letterSpacing: '2.5px',
              textTransform: 'uppercase',
              cursor: (isOutOfStock || isAddingToCart) ? 'not-allowed' : 'pointer',
              opacity: (isOutOfStock || isAddingToCart) ? 0.55 : 1,
              fontWeight: 500,
            }}
            onMouseEnter={(e) => {
              if (isOutOfStock || isAddingToCart) return;
              e.currentTarget.style.background = 'var(--ink)';
              e.currentTarget.style.color = 'var(--bg)';
            }}
            onMouseLeave={(e) => {
              if (isOutOfStock || isAddingToCart) return;
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--ink)';
            }}
          >
            {isAddingToCart && <Loader2 className="h-3 w-3 animate-spin" />}
            {isOutOfStock
              ? t('products.outOfStock')
              : isAddingToCart
              ? t('common.loading')
              : hasMultipleSizes
              ? t('products.selectSize')
              : t('products.addToCart')}
          </button>
        </div>
      </div>

      {/* Quick View Modal */}
      <QuickViewModal
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
        product={product}
      />
    </>
  );
};

export default ProductCard;
